import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { BRAND_COLORS, STATUS_COLORS } from "./bookingStatus";

/**
 * Global ConfirmProvider (mounted at App root).
 * - Use the imperative helper `confirmDialog(options)` from anywhere.
 * - Or the hook `useConfirm()` from inside a component.
 * Both return Promise<boolean>. Fallback to native `window.confirm` if the
 * provider isn't mounted yet, so components stay safe during hot-reload.
 */

const DEFAULTS = {
  title: "Bist du sicher?",
  description: "",
  confirmText: "Bestätigen",
  cancelText: "Abbrechen",
  variant: "default", // "default" | "danger"
};

// Module-singleton so any file can call confirmDialog() without hook wiring.
let _confirmFn = null;

export function confirmDialog(options = {}) {
  if (typeof _confirmFn === "function") return _confirmFn(options);
  // Provider not mounted (SSR / very early paint) → fallback to native
  if (typeof window !== "undefined" && window.confirm) {
    return Promise.resolve(window.confirm(options.description || options.title || "Bist du sicher?"));
  }
  return Promise.resolve(false);
}

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({ ...DEFAULTS, ...options });
    setOpen(true);
    return new Promise((resolve) => { resolverRef.current = resolve; });
  }, []);

  // Expose to module-level singleton so non-hook callers work too.
  useEffect(() => {
    _confirmFn = confirm;
    return () => { if (_confirmFn === confirm) _confirmFn = null; };
  }, [confirm]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);
  const handleConfirm = useCallback(() => {
    setOpen(false);
    resolverRef.current?.(true);
    resolverRef.current = null;
  }, []);

  const value = useMemo(() => confirm, [confirm]);
  const isDanger = opts.variant === "danger";

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) handleCancel(); }}>
        <AlertDialogContent
          data-testid="confirm-dialog"
          className="border-navy/15 rounded-none p-0 gap-0"
          style={{ background: BRAND_COLORS.ivory }}
        >
          <div className="px-6 pt-6 pb-4 border-b border-navy/10">
            <AlertDialogHeader>
              <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>Bestätigung</div>
              <AlertDialogTitle className="font-display text-2xl text-navy leading-tight">
                {opts.title}
              </AlertDialogTitle>
              {opts.description && (
                <AlertDialogDescription className="text-[13.5px] text-navy-dark/75 leading-relaxed pt-2 whitespace-pre-line">
                  {opts.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
          </div>
          <AlertDialogFooter className="px-6 py-4 gap-2">
            <AlertDialogCancel
              onClick={handleCancel}
              data-testid="confirm-cancel"
              className="rounded-none border-navy/25 text-navy hover:bg-navy hover:text-ivory text-[11px] tracking-[0.15em] uppercase mt-0"
            >
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              data-testid="confirm-ok"
              className="rounded-none text-ivory text-[11px] tracking-[0.15em] uppercase hover:opacity-90"
              style={isDanger
                ? { background: STATUS_COLORS.red, borderColor: STATUS_COLORS.red }
                : { background: BRAND_COLORS.navy, borderColor: BRAND_COLORS.navy }}
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  return ctx || confirmDialog;
}
